import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Phone, Mail, ArrowUpRight, Send, CheckCircle, AlertCircle, MapPin, Navigation } from 'lucide-react';
import whatsappIcon from '../assets/WhatsappICON.png';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { trackEvent } from '../utils/analytics';

const contactSchema = z.object({
  fullName: z.string().min(2, { message: "Ad Soyad en az 2 karakter olmalıdır." }).max(100, { message: "Ad Soyad en fazla 100 karakter olabilir." }),
  company: z.string().min(1, { message: "Marka / Şirket adı gereklidir." }).max(120, { message: "Şirket adı en fazla 120 karakter olabilir." }),
  email: z.string().email({ message: "Geçerli bir e-posta adresi giriniz." }).max(150, { message: "E-posta adresi en fazla 150 karakter olabilir." }),
  phone: z.string().min(10, { message: "Geçerli bir telefon numarası giriniz." }).max(30, { message: "Telefon numarası en fazla 30 karakter olabilir." }),
  service: z.string().min(1, { message: "Lütfen ilgilendiğiniz hizmeti seçiniz." }),
  timeline: z.string().min(1, { message: "Lütfen tahmini zamanlamayı belirtiniz." }),
  budget: z.string().min(1, { message: "Lütfen tahmini bütçe aralığını belirtiniz." }),
  summary: z.string().min(10, { message: "Lütfen proje özetini en az 10 karakterle açıklayınız." }).max(3000, { message: "Proje özeti en fazla 3000 karakter olabilir." }),
  kvkk: z.boolean().refine(val => val === true, { message: "Devam etmek için şartları kabul etmelisiniz." }),
  website_url_check: z.string().optional() // Anti-bot honeypot
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.contact : en.contact;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      kvkk: false,
      website_url_check: ''
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      setSubmitError('');
      
      // Bot Detection via Honeypot
      if (data.website_url_check && data.website_url_check.trim().length > 0) {
        console.warn("Spam bot detected and blocked.");
        setIsSubmitted(true);
        reset();
        return;
      }

      const endpoint = '/api/contact.php';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.fullName.trim(),
          company: data.company.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          service: data.service,
          timeline: data.timeline,
          budget: data.budget,
          message: data.summary.trim(),
          website_hp: data.website_url_check || ''
        })
      });

      const resData = await response.json().catch(() => ({}));

      if (!response.ok || !resData.success) {
        throw new Error(
          resData.error || 
          (language === 'TR' 
            ? 'Form iletilirken bir sunucu hatası oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyin veya doğrudan WhatsApp ile iletişime geçin.' 
            : 'An error occurred while submitting the form. Please retry or contact us directly via WhatsApp.')
        );
      }

      // Only on HTTP 2xx success:
      setIsSubmitted(true);
      
      // Fire GA4 conversion event (No PII)
      trackEvent('generate_lead', {
        event_category: 'Contact Form',
        event_label: data.service,
        service_category: data.service,
        form_location: 'contact_page',
        locale: language
      });

      reset();
    } catch (err: any) {
      setSubmitError(err.message || (language === 'TR' ? 'Form gönderilemedi. Lütfen tekrar deneyin veya doğrudan WhatsApp ile iletişime geçin.' : 'Submission failed. Please try again or contact us directly via WhatsApp.'));
    }
  };

  const servicesList = language === 'TR' ? [
    "Dijital ve Sosyal Medya",
    "Video Prodüksiyon",
    "Canlı Yayın ve Program",
    "Post-Prodüksiyon",
    "Tasarım ve Marka",
    "Web ve Yazılım",
    "Diğer / Entegre Kampanya"
  ] : [
    "Digital & Social Media Strategy",
    "Film & Video Production",
    "Live Broadcasting & Reji",
    "Post-Production & VFX",
    "Brand Identity & Design",
    "Web & Custom Software",
    "Other / Integrated Campaign"
  ];

  return (
    <div className="pt-28 pb-16 bg-background">
      <Helmet>
        <title>{language === 'TR' ? "İletişim - WE24 Agency" : "Contact Us - WE24 Agency"}</title>
        <meta
          name="description"
          content={
            language === 'TR'
              ? "Proje bilgi formunu doldurarak bütçe ve hedeflerinizi paylaşın ya da stüdyomuzla doğrudan iletişime geçin."
              : "Contact WE24 Agency Istanbul for project inquiries, proposals, or direct studio communication."
          }
        />
        <link rel="canonical" href={language === 'TR' ? "https://www.we24agency.com/iletisim/" : "https://www.we24agency.com/en/contact/"} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "WE24 Agency İletişim",
            "description": "Proje teklif ve stüdyo iletişim kanalları.",
            "url": language === 'TR' ? "https://www.we24agency.com/iletisim/" : "https://www.we24agency.com/en/contact/",
            "mainEntity": {
              "@type": "Organization",
              "name": "WE24 Agency",
              "telephone": "+90 545 229 24 24",
              "email": "info@we24agency.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Meşrutiyet Mah. Şehit İsmet Armağan Sok. No: 7",
                "addressLocality": "Nişantaşı, Şişli",
                "addressRegion": "İstanbul",
                "postalCode": "34363",
                "addressCountry": "TR"
              }
            }
          })}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: Contact details */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="max-w-md">
              <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-semibold mb-2 block">
                {language === 'TR' ? "İletişim" : "Contact"}
              </span>
              <h1 className="text-4xl sm:text-5xl font-display text-white mb-4">
                {t.title}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t.subtitle}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {/* WhatsApp Card */}
              <a
                href="https://wa.me/905452292424"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { placement: 'contact_page' })}
                className="liquid-glass border border-white/10 hover:border-emerald-500/40 p-5 rounded-2xl flex items-center justify-between group transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                    <img src={whatsappIcon} alt="WhatsApp" className="w-6 h-6 object-contain group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">WhatsApp</h3>
                    <p className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">+90 545 229 24 24</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>

              {/* Email Card */}
              <a
                href="mailto:info@we24agency.com"
                onClick={() => trackEvent('email_click', { placement: 'contact_page' })}
                className="liquid-glass border border-white/10 hover:border-[#ff7a63]/40 p-5 rounded-2xl flex items-center justify-between group transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#ff7a63]/10 border border-[#ff7a63]/20 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-[#ff7a63] group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t.email}</h3>
                    <p className="text-base font-bold text-white group-hover:text-[#ff7a63] transition-colors">info@we24agency.com</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#ff7a63] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>

              {/* Phone Card */}
              <a
                href="tel:+905452292424"
                className="liquid-glass border border-white/10 hover:border-blue-500/40 p-5 rounded-2xl flex items-center justify-between group transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-blue-400 group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t.phone}</h3>
                    <p className="text-base font-bold text-white group-hover:text-blue-400 transition-colors">+90 545 229 24 24</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-blue-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>

              {/* Address Card */}
              <a
                href="https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul"
                target="_blank"
                rel="noopener noreferrer"
                className="liquid-glass border border-white/10 hover:border-amber-500/40 p-5 rounded-2xl flex items-center justify-between group transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-amber-400 group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{language === 'TR' ? "Stüdyo & Ofis" : "Studio & Office"}</h3>
                    <p className="text-sm font-bold text-white group-hover:text-amber-400 transition-colors">Meşrutiyet Mah. Şehit İsmet Armağan Sok. No: 7, Nişantaşı, Şişli, İstanbul</p>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right Column: Contact form */}
          <div className="lg:col-span-7 bg-card/15 border border-border/20 p-8 sm:p-10 rounded-3xl">
            {isSubmitted ? (
              <div className="text-center py-12 flex flex-col items-center gap-6" aria-live="polite">
                <CheckCircle className="w-16 h-16 text-emerald-500" />
                <div className="flex flex-col gap-2">
                  <h3 className="text-2xl font-display text-white">{t.formSuccessTitle}</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                    {t.formSuccessDesc}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSubmitted(false)}
                  className="px-6 py-2.5 bg-white text-black text-xs font-bold uppercase tracking-wider rounded-full hover:bg-white/90 transition-all mt-4"
                >
                  {t.sendNewForm}
                </button>
              </div>
            ) : (
              <form
                method="POST"
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
                noValidate
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-bold text-white">{t.formTitle}</h3>
                  <span className="text-[11px] text-[#ff7a63] font-medium hidden sm:inline">
                    {language === 'TR' ? "⚡ 1 iş günü içinde dönüş" : "⚡ 1 business day turnaround"}
                  </span>
                </div>

                {/* Anti-spam Bot Honeypot (Invisible to humans) */}
                <div className="hidden opacity-0 pointer-events-none absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url_check">Do not fill this field</label>
                  <input
                    type="text"
                    id="website_url_check"
                    tabIndex={-1}
                    autoComplete="off"
                    {...register('website_url_check')}
                  />
                </div>

                {submitError && (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl flex items-center gap-3 text-sm text-red-400" aria-live="polite">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="fullName" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.fullName} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      required
                      aria-required="true"
                      autoComplete="name"
                      aria-describedby={errors.fullName ? "fullName-error" : undefined}
                      {...register('fullName')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all ${
                        errors.fullName ? 'border-red-500/80 bg-red-500/5' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                      placeholder={language === 'TR' ? "Adınız Soyadınız" : "Your full name"}
                    />
                    {errors.fullName && (
                      <span id="fullName-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.fullName.message}
                      </span>
                    )}
                  </div>

                  {/* Company */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="company" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.company} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      required
                      aria-required="true"
                      autoComplete="organization"
                      aria-describedby={errors.company ? "company-error" : undefined}
                      {...register('company')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all ${
                        errors.company ? 'border-red-500/80 bg-red-500/5' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                      placeholder={language === 'TR' ? "Şirketinizin Adı" : "Company or brand name"}
                    />
                    {errors.company && (
                      <span id="company-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.company.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Email */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.email} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      aria-required="true"
                      autoComplete="email"
                      aria-describedby={errors.email ? "email-error" : undefined}
                      {...register('email')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all ${
                        errors.email ? 'border-red-500/80 bg-red-500/5' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                      placeholder={language === 'TR' ? "E-posta Adresiniz" : "name@company.com"}
                    />
                    {errors.email && (
                      <span id="email-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.email.message}
                      </span>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.phone} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      required
                      aria-required="true"
                      autoComplete="tel"
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                      {...register('phone')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all ${
                        errors.phone ? 'border-red-500/80 bg-red-500/5' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                      placeholder={language === 'TR' ? "+90 5XX XXX XX XX" : "+90 ..."}
                    />
                    {errors.phone && (
                      <span id="phone-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.phone.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Service Dropdown */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="service" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                    {t.service} <span className="text-[#ff7a63]">*</span>
                  </label>
                  <select
                    id="service"
                    required
                    aria-required="true"
                    aria-describedby={errors.service ? "service-error" : undefined}
                    {...register('service')}
                    className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all cursor-pointer ${
                      errors.service ? 'border-red-500/80' : 'border-border/30 focus:border-[#ff7a63]'
                    }`}
                  >
                    <option value="">{t.selectService}</option>
                    {servicesList.map(s => <option key={s} value={s} className="bg-[#0b1b2b]">{s}</option>)}
                  </select>
                  {errors.service && (
                    <span id="service-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                      {errors.service.message}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Timeline */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="timeline" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.timeline} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <select
                      id="timeline"
                      required
                      aria-required="true"
                      aria-describedby={errors.timeline ? "timeline-error" : undefined}
                      {...register('timeline')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all cursor-pointer ${
                        errors.timeline ? 'border-red-500/80' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                    >
                      <option value="">{t.selectTimeline}</option>
                      {language === 'TR' ? (
                        <>
                          <option value="Hemen (1 ay içinde)" className="bg-[#0b1b2b]">Hemen (1 ay içinde)</option>
                          <option value="Yakın Zamanda (1-3 ay)" className="bg-[#0b1b2b]">Yakın Zamanda (1-3 ay)</option>
                          <option value="Planlama Aşamasında" className="bg-[#0b1b2b]">Planlama Aşamasında</option>
                        </>
                      ) : (
                        <>
                          <option value="Immediate (Within 1 month)" className="bg-[#0b1b2b]">Immediate (Within 1 month)</option>
                          <option value="Near Term (1-3 months)" className="bg-[#0b1b2b]">Near Term (1-3 months)</option>
                          <option value="Planning Phase" className="bg-[#0b1b2b]">Planning Phase</option>
                        </>
                      )}
                    </select>
                    {errors.timeline && (
                      <span id="timeline-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.timeline.message}
                      </span>
                    )}
                  </div>

                  {/* Budget */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="budget" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                      {t.budget} <span className="text-[#ff7a63]">*</span>
                    </label>
                    <select
                      id="budget"
                      required
                      aria-required="true"
                      aria-describedby={errors.budget ? "budget-error" : undefined}
                      {...register('budget')}
                      className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all cursor-pointer ${
                        errors.budget ? 'border-red-500/80' : 'border-border/30 focus:border-[#ff7a63]'
                      }`}
                    >
                      <option value="">{t.selectBudget}</option>
                      {language === 'TR' ? (
                        <>
                          <option value="50.000 TL - 100.000 TL" className="bg-[#0b1b2b]">50.000 TL - 100.000 TL</option>
                          <option value="100.000 TL - 250.000 TL" className="bg-[#0b1b2b]">100.000 TL - 250.000 TL</option>
                          <option value="250.000 TL+" className="bg-[#0b1b2b]">250.000 TL+</option>
                          <option value="Belirtmek İstemiyorum" className="bg-[#0b1b2b]">Belirtmek İstemiyorum</option>
                        </>
                      ) : (
                        <>
                          <option value="€2,000 - €5,000" className="bg-[#0b1b2b]">€2,000 - €5,000</option>
                          <option value="€5,000 - €10,000" className="bg-[#0b1b2b]">€5,000 - €10,000</option>
                          <option value="€10,000+" className="bg-[#0b1b2b]">€10,000+</option>
                          <option value="Prefer not to say" className="bg-[#0b1b2b]">Prefer not to say</option>
                        </>
                      )}
                    </select>
                    {errors.budget && (
                      <span id="budget-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                        {errors.budget.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Project Summary */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="summary" className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                    {t.summary} <span className="text-[#ff7a63]">*</span>
                  </label>
                  <textarea
                    id="summary"
                    rows={4}
                    required
                    aria-required="true"
                    aria-describedby={errors.summary ? "summary-error" : undefined}
                    {...register('summary')}
                    className={`bg-card/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#ff7a63] transition-all resize-none ${
                      errors.summary ? 'border-red-500/80 bg-red-500/5' : 'border-border/30 focus:border-[#ff7a63]'
                    }`}
                    placeholder={language === 'TR' ? "İhtiyaçlarınızı ve proje detaylarını kısaca özetleyin..." : "Summarize your project goals and requirements..."}
                  />
                  {errors.summary && (
                    <span id="summary-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                      {errors.summary.message}
                    </span>
                  )}
                </div>

                {/* KVKK Checkbox */}
                <div className="flex flex-col gap-1.5 mt-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="kvkk"
                      required
                      aria-required="true"
                      aria-describedby={errors.kvkk ? "kvkk-error" : undefined}
                      {...register('kvkk')}
                      className="w-4 h-4 rounded border-border bg-card text-[#ff7a63] focus:ring-0 mt-0.5 cursor-pointer accent-[#ff7a63]"
                    />
                    <span className="text-xs text-muted-foreground leading-snug">
                      <Link to={language === 'TR' ? "/kvkk" : "/en/kvkk"} target="_blank" className="text-white underline hover:text-[#ff7a63] transition-colors">
                        {language === 'TR' ? "KVKK Aydınlatma Metni" : "Privacy & KVKK Policy"}
                      </Link>
                      {language === 'TR'
                        ? "'ni okudum, anladım ve verilerimin işlenmesini kabul ediyorum. *"
                        : " terms have been read and agreed to. *"}
                    </span>
                  </label>
                  {errors.kvkk && (
                    <span id="kvkk-error" className="text-[10px] text-red-400 font-medium" aria-live="polite">
                      {errors.kvkk.message}
                    </span>
                  )}
                </div>

                {/* Error Banner with Retry & WhatsApp Options */}
                {submitError && (
                  <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 flex flex-col gap-3 text-red-400 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-400" />
                      <div className="text-xs leading-relaxed text-red-200">
                        {submitError}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-red-500/20">
                      <a
                        href="https://wa.me/905452292424?text=Merhaba,%20web%20sitenizdeki%20ileti%C5%9Fim%20formu%20%C3%BCzerinden%20size%20ula%C5%9Fmak%20istemi%C5%9Ftim."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors"
                      >
                        <img src={whatsappIcon} alt="WhatsApp" className="w-3.5 h-3.5" />
                        <span>{language === 'TR' ? 'WhatsApp ile İletişime Geç' : 'Contact via WhatsApp'}</span>
                      </a>
                    </div>
                  </div>
                )}

                {/* Submit button with loading state */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-2">
                  <p className="text-[11px] text-zinc-400 order-2 sm:order-1">
                    {language === 'TR'
                      ? "Tüm proje ve teklif taleplerinize en geç 1 iş günü içinde detaylı geri dönüş sağlıyoruz."
                      : "We review all project proposals and respond within 1 business day."}
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto order-1 sm:order-2 px-8 py-3.5 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-bold uppercase tracking-wider rounded-full transition-all inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 shadow-xl shrink-0"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
                        <span>{language === 'TR' ? 'Gönderiliyor...' : 'Sending...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{t.submit}</span>
                        <Send className="w-3.5 h-3.5 text-[#ff7a63]" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Interactive Google Maps Section */}
        <div className="mt-16 sm:mt-20 border border-white/[0.08] bg-card/20 rounded-3xl overflow-hidden p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ff7a63]/10 border border-[#ff7a63]/20 text-[#ff7a63] text-xs font-bold uppercase tracking-wider mb-2">
                <MapPin className="w-3.5 h-3.5" />
                <span>{language === 'TR' ? "STÜDYO VE OFİS KONUMUMUZ" : "STUDIO & OFFICE LOCATION"}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-display text-white">
                {language === 'TR' ? "Nişantaşı, İstanbul'dayız" : "Located in Nişantaşı, Istanbul"}
              </h2>
              <p className="text-xs sm:text-sm text-zinc-300 mt-2 max-w-3xl leading-relaxed">
                {language === 'TR' 
                  ? "Nişantaşı’nın kalbinde, dünya markalarıyla aynı atmosferi paylaşıyoruz. Osmanbey metro istasyonuna yürüyüş mesafesindeki merkezi stüdyomuzda; prodüksiyon ve post-prodüksiyon süreçlerinize global bir vizyon, şehrin enerjisiyle hayat veriyoruz."
                  : "Located in the heart of Nişantaşı, sharing the vibrant pulse of global brands. Just steps from Osmanbey Metro Station, our studio breathes life into your production and post-production workflows with world-class vision and metropolitan energy."}
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <a
                href="https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 font-bold text-xs uppercase tracking-wider transition-all inline-flex items-center gap-2 shadow-lg"
              >
                <Navigation className="w-3.5 h-3.5 text-[#ff7a63]" />
                <span>{language === 'TR' ? "Yol Tarifi Al" : "Get Directions"}</span>
              </a>
            </div>
          </div>

          {/* Google Maps Iframe */}
          <div className="relative w-full h-[360px] sm:h-[450px] rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 shadow-2xl">
            <iframe
              title="WE24 Agency Nişantaşı Stüdyo Konumu"
              src="https://maps.google.com/maps?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul&t=&z=16&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(0.15) contrast(1.05) invert(0.9) hue-rotate(180deg)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
